import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { FormControlSizes } from '../types';

type ToggleGroupIndicator = {
  x: number;
  y: number;
  width: number;
  height: number;
  visible: boolean;
};

type UseToggleGroupIndicatorParams = {
  useSlideAnimation: boolean;
  value: string | undefined;
  options: Array<unknown>;
  orientation: 'horizontal' | 'vertical';
  size: FormControlSizes;
  rootRef: RefObject<HTMLDivElement | null>;
  itemRefs: RefObject<Record<string, HTMLDivElement | null>>;
};

const hiddenIndicator: ToggleGroupIndicator = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  visible: false,
};

const isSameIndicator = (current: ToggleGroupIndicator, next: ToggleGroupIndicator) =>
  current.x === next.x &&
  current.y === next.y &&
  current.width === next.width &&
  current.height === next.height &&
  current.visible === next.visible;

export const useToggleGroupIndicator = ({
  useSlideAnimation,
  value,
  options,
  orientation,
  size,
  rootRef,
  itemRefs,
}: UseToggleGroupIndicatorParams): ToggleGroupIndicator => {
  const [indicator, setIndicator] = useState<ToggleGroupIndicator>(hiddenIndicator);
  const updateIndicatorRef = useRef<(() => void) | null>(null);

  const updateIndicator = useCallback(() => {
    if (!useSlideAnimation) {
      return;
    }

    const hideIndicator = () => {
      setIndicator(prev => (prev.visible ? { ...prev, visible: false } : prev));
    };

    if (value == null) {
      hideIndicator();
      return;
    }

    const root = rootRef.current;
    const items = itemRefs.current;
    const item = items == null ? null : items[String(value)];
    if (root == null || item == null) {
      hideIndicator();
      return;
    }

    // offset* (layout space, relative to offsetParent) is unaffected by ancestor transforms like Modal's enter animation, unlike getBoundingClientRect().
    const next = {
      x: item.offsetLeft,
      y: item.offsetTop,
      width: item.offsetWidth,
      height: item.offsetHeight,
      visible: true,
    };
    setIndicator(prev => (isSameIndicator(prev, next) ? prev : next));
  }, [itemRefs, rootRef, useSlideAnimation, value]);

  useLayoutEffect(() => {
    updateIndicatorRef.current = updateIndicator;
  }, [updateIndicator]);

  useLayoutEffect(() => {
    updateIndicatorRef.current?.();
  }, [options, orientation, size, value, useSlideAnimation]);

  useEffect(() => {
    if (!useSlideAnimation) {
      return;
    }

    const root = rootRef.current;
    const handleResize = () => updateIndicatorRef.current?.();

    if (root != null && typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(root);
      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [rootRef, useSlideAnimation]);

  return indicator;
};
